import { CSSProperties } from 'react';
import { QuestionIcon } from '@phosphor-icons/react';
import { IconButton } from '../IconButton';
import { Tooltip, type TooltipPlacement } from '../Tooltip';

export interface InfoTooltipProps {
  /** Explanation shown on hover and keyboard focus; also the icon's accessible name */
  content: string;
  /** Placement of tooltip relative to the icon */
  placement?: TooltipPlacement;
  /** Additional className */
  className?: string;
  /** Additional style */
  style?: CSSProperties;
}

/**
 * InfoTooltip component
 *
 * A "?" icon that explains the element next to it. Opens on hover and on
 * keyboard focus.
 *
 * @example
 * ```tsx
 * import { InfoTooltip } from '@libertify/ui';
 *
 * <Flex align="center" gap={4}>
 *   {label}
 *   <InfoTooltip content={t('metric.info')} />
 * </Flex>
 * ```
 */
export const InfoTooltip = ({
  content,
  placement = 'top',
  className,
  style,
}: InfoTooltipProps) => (
  <Tooltip content={content} placement={placement} trigger={['hover', 'focus']}>
    <IconButton
      variant="tertiary"
      size="xs"
      shape="round"
      icon={<QuestionIcon aria-hidden />}
      aria-label={content}
      className={className}
      style={style}
    />
  </Tooltip>
);

export default InfoTooltip;
